import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, CircularProgress } from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Blacklist from "./Blacklist";
import Blacklist2 from "./Blacklist2";
import useUnifiedInfiniteScroll from "../../hooks/useUnifiedInfiniteScroll";
import InfiniteScrollIndicator from "../../components/InfiniteScrollIndicator";

const apiUrl = process.env.REACT_APP_API_URL;

const BlacklistInfiniteScroll = ({
  viewType,
  setViewType,
  onNavigateToApplicationTable = null,
  onNavigateToDynamicForms = null,
  onUnblock = null,
  infiniteScrollPageSize = 2,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const initializationRef = useRef(false);

  // Helper function to parse JWT token
  const parseJwt = (token) => {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  };

  // Helper function to get user ID from access token
  const getUserIdFromAccessToken = () => {
    const accessToken = localStorage.getItem("accessToken");
    if (!accessToken) {
      console.error("No access token found in local storage");
      return null;
    }

    try {
      const decodedToken = parseJwt(accessToken);
      return decodedToken.user_id;
    } catch (error) {
      console.error("Failed to decode access token", error);
      return null;
    }
  };

  // Create fetch function specifically for blacklist
  const fetchBlacklistData = useCallback(async (page, pageSize, searchParams = {}) => {
    const recruiterId = getUserIdFromAccessToken();
    if (!recruiterId) {
      console.error("No recruiter ID available");
      return { results: [], count: 0, hasNext: false };
    }

    const endpoint = `${apiUrl}candidates/api/blacklist/${recruiterId}/`;

    const params = {
      page,
      page_size: pageSize,
      ...searchParams,
    };

    console.log("🔍 BLACKLIST INFINITE SCROLL - Making API call to:", endpoint, "with params:", params);

    try {
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
        params,
      });

      const responseData = response.data;
      let results = [];
      let count = 0;
      let hasNext = false;

      if (responseData?.status === "success") {
        // Old format from authentication endpoint
        const normalizedData = responseData.blacklisted_candidates.map(
          (item) => ({
            ...item.candidate,
            blacklist_id: item.id,
            candidate_id: item.candidate_id,
            reason: item.reason,
            date_blacklisted: item.date_blacklisted,
          })
        );
        results = normalizedData || [];
        count = results.length;
        hasNext = false;
      } else if (responseData.results) {
        // New paginated format from candidates endpoint
        results = responseData.results || [];
        count = responseData.count || results.length;
        hasNext = responseData.next !== null;
      } else if (Array.isArray(responseData)) {
        results = responseData;
        count = results.length;
        hasNext = false;
      }

      console.log("🔍 BLACKLIST INFINITE SCROLL - Returning results:", {
        resultsCount: results.length,
        totalCount: count,
        hasNext: hasNext
      });

      return {
        results,
        count,
        hasNext,
      };
    } catch (error) {
      console.error("Error fetching blacklist data:", error);
      return { results: [], count: 0, hasNext: false };
    }
  }, []);

  // Initialize unified infinite scroll hook specifically for blacklist
  const {
    data: infiniteScrollData,
    loading: infiniteScrollLoading,
    hasMore,
    loadMore,
    reset: resetInfiniteScroll,
    loadInitial: loadInitialInfiniteScroll,
    setDataDirectly: setInfiniteScrollDataDirectly,
    error: infiniteScrollError,
    totalCount: infiniteScrollTotalCount,
    lastElementRef,
  } = useUnifiedInfiniteScroll({
    fetchData: fetchBlacklistData,
    initialData: [],
    initialTotalCount: 0,
    pageSize: infiniteScrollPageSize,
    dependencies: [], // No dependencies to prevent resets
    enabled: true,
  });

  // Initialize data on component mount
  useEffect(() => {
    // Prevent multiple initializations
    if (initializationRef.current) {
      console.log("🚀 BLACKLIST INFINITE SCROLL - Skipping re-initialization");
      return;
    }

    const initializeData = async () => {
      initializationRef.current = true;
      setLoading(true);
      try {
        console.log("🚀 BLACKLIST INFINITE SCROLL - Initializing data...");
        await loadInitialInfiniteScroll();
      } catch (error) {
        console.error("Error initializing blacklist data:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []); // Empty dependency array to prevent re-initialization

  // Handle search results for blacklist
  const handleSearchResults = (results, totalCount = null, searchParams = {}) => {
    console.log("🔍 BLACKLIST SEARCH - handleSearchResults called with:", {
      results: results,
      totalCount: totalCount,
      searchParams: searchParams
    });

    // Handle null results (clear button clicked)
    if (results === null) {
      console.log("🔍 BLACKLIST SEARCH - Clear button clicked, resetting...");
      resetInfiniteScroll();
      setTimeout(() => {
        loadInitialInfiniteScroll();
      }, 100);
      return;
    }

    if (Array.isArray(results)) {
      const newTotalCount = totalCount !== null ? totalCount : results.length;
      // Preserve scroll position when applying search/filters (true by default)
      setInfiniteScrollDataDirectly(results, newTotalCount, true);
    } else {
      console.error("Blacklist search results are not an array:", results);
      resetInfiniteScroll();
    }
  };

  // Handle successful unblock - refresh the data
  const handleUnblockSuccess = () => {
    console.log("🔍 BLACKLIST - Unblock successful, refreshing data...");
    resetInfiniteScroll();
    setTimeout(() => {
      loadInitialInfiniteScroll();
    }, 100);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {viewType === "card" ? (
        <Blacklist
          candidates={infiniteScrollData}
          post_title=""
          onUnblock={onUnblock}
          onUnblockSuccess={handleUnblockSuccess}
          hideSearchBar={false}
          location=""
          initialTotalCount={infiniteScrollTotalCount}
          onNavigateToApplicationTable={onNavigateToApplicationTable}
          onNavigateToDynamicForms={onNavigateToDynamicForms}
          enableInfiniteScroll={true}
          infiniteScrollPageSize={infiniteScrollPageSize}
          fetchMoreData={fetchBlacklistData}
          onSearchResults={handleSearchResults}
          infiniteScrollData={infiniteScrollData}
          infiniteScrollTotalCount={infiniteScrollTotalCount}
        />
      ) : (
        <Blacklist2
          data={infiniteScrollData}
          viewType={viewType}
          setViewType={setViewType}
          onDataUpdate={() => {}} // Not needed for infinite scroll
          initialTotalCount={infiniteScrollTotalCount}
        />
      )}

      {/* Infinite Scroll Loading Indicator */}
      <InfiniteScrollIndicator
        loading={infiniteScrollLoading}
        hasMore={hasMore}
        dataLength={infiniteScrollData.length}
        error={infiniteScrollError}
        lastElementRef={lastElementRef}
        loadingText="applicationTable.loadingMore"
        noMoreText="applicationTable.noMoreCandidates"
        errorText="applicationTable.loadingError"
      />
    </Box>
  );
};

export default React.memo(BlacklistInfiniteScroll);

